import { useCallback, useEffect } from "react";
import { useSalesforce } from "../hooks/useSalesforce";
import { IncidentQueryField } from "../types/salesforce";
import { Card, CardContent } from "./UI/card";
import { Search } from "lucide-react";
import { useQueryBuilder } from "../hooks/useQueryBuilder";

interface Field {
  className: string;
}

export function Sidebar({ className }: Readonly<Field>) {
  const { getQueryFields, isLoading, error } = useSalesforce();

  const {
    availableFields,
    setAvailableFields,
    selectedFields,
    setSelectedFields,
  } = useQueryBuilder();

  useEffect(() => {
    const loadFields = async () => {
      try {
        const fields = await getQueryFields();
        setAvailableFields(fields);
      } catch (error) {
        console.error("Failed to load fields:", error);
      }
    };
    loadFields();
  }, [getQueryFields]);

  const handleSelect = useCallback(
    (field: IncidentQueryField) => {
      // Create new arrays and pass them directly
      const newSelectedFields = [...selectedFields, field];
      setSelectedFields(newSelectedFields);

      const newAvailableFields = availableFields.filter(
        (f) => f.DeveloperName !== field.DeveloperName
      );
      setAvailableFields(newAvailableFields);
    },
    [selectedFields, availableFields]
  );

  const handleSelectAll = useCallback(() => {
    // Add all available fields to selected
    const newSelectedFields = [...selectedFields, ...availableFields];
    setSelectedFields(newSelectedFields);
    // Clear available fields
    setAvailableFields([]);
  }, [selectedFields, availableFields]);

  const handleDeselectAll = useCallback(() => {
    // Move all selected fields back to available
    const newAvailableFields = [...availableFields, ...selectedFields];
    setAvailableFields(newAvailableFields);
    // Clear selected fields
    setSelectedFields([]);
  }, [availableFields, selectedFields]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex h-[300px] flex-col items-center justify-center rounded-md border p-4 text-gray-500 md:h-[400px] md:p-6">
            <Search className="mb-2 h-8 w-8 md:h-12 md:w-12" />
            <p className="text-sm md:text-base">Loading fields...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-red-500">
          <div className="flex h-[300px] flex-col items-center justify-center rounded-md border p-4 text-gray-500 md:h-[400px] md:p-6">
            <Search className="mb-2 h-8 w-8 md:h-12 md:w-12" />
            <p className="text-sm md:text-base">Error: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    // <div className={`bg-white w-1/3 border-[0.5px] border-r p-4 pr-8`}>
    <div className="bg-white w-1/3 border-[0.5px] border-r p-4 pr-8">
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-600">
          All available Fields
        </h2>
        <button
          onClick={
            availableFields.length > 0 ? handleSelectAll : handleDeselectAll
          }
          className={`flex w-full cursor-pointer items-center justify-between rounded-md border-0 px-4 py-2 ${
            availableFields.length > 0
              ? "bg-purple-400 text-white-main"
              : "bg-gray-400 text-white-main"
          }`}
        >
          <span>
            {availableFields.length > 0
              ? "Add All Fields"
              : "Remove All Fields"}
          </span>
          <span className="ml-2 text-xl">
            {availableFields.length > 0 ? "+" : "−"}
          </span>
        </button>
        <div className="h-[600px] space-y-2 overflow-y-auto rounded-md border p-4 md:h-[700px] md:p-6">
          {availableFields.map((field) => (
            <div
              key={field.DeveloperName}
              className="group flex items-center justify-between pt-1"
            >
              <span className="text-sm text-gray-500">{field.Label}</span>
              <button
                onClick={() => handleSelect(field)}
                className="cursor-pointer border-[0.5px] bg-white-main p-1 px-2 text-sm hover:text-purple-400"
              >
                +
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
